import * as React from "react";
import {
  Card,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import Visibility from "@material-ui/icons/Visibility";
import DottedLine from "../DottedLine";
import { formatDateTime } from "../../helpers/datetime";
import clsx from "clsx";
import { EMPTY_STRING_LABEL } from "../../core/constants/appGlobals";
import { padN } from "../../helpers/utility";
import { ADMIN, INTERNAL, SUPER_ADMIN } from "../../core/models/GroupTypeEnum";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "maxWidth": 500,
      "minHeight": 370,

      "& .margin-top": {
        marginTop: theme.spacing(3),
      },
    },

    header: {
      "display": "flex",
      "flexWrap": "wrap",
      "justifyContent": "space-between",
      "alignItems": "center",
      "padding": theme.spacing(1, 2, 0, 2),

      "& h2": {
        color: theme.palette.primary.main,
        fontSize: "1.35rem",
        fontWeight: "bold",
      },
    },

    greenHeader: {
      "display": "flex",
      "flexWrap": "wrap",
      "justifyContent": "space-between",
      "alignItems": "center",
      "padding": theme.spacing(1, 2, 0, 2),
      "backgroundColor": theme.palette.primary.main,

      "& h2": {
        color: "white",
        fontSize: "1.35rem",
        fontWeight: "bold",
      },

      "& svg": {
        color: "white",
      },
    },

    main: {
      "padding": theme.spacing(2),

      "& p": {
        color: theme.customPalette.text.primary,
        fontWeight: "bolder",
      },

      "& h5": {
        fontWeight: "bolder",
        fontSize: "smaller",
      },

      "& .flex-column": {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      },

      "& .grid": {
        display: "grid",
        gridTemplateColumns: "auto auto",
        gridRowGap: theme.spacing(4),
        gridColumnGap: theme.spacing(1),
      },

      "& .flex": {
        display: "flex",
        alignItems: "flex-start",
        flexWrap: "wrap",
      },

      "& .small": {
        fontSize: "small",
      },

      "& .large": {
        fontSize: "1.5rem",
        fontWeight: "bold",
      },

      "& .margin-right": {
        marginRight: theme.spacing(2),
      },

      "& .expiration-color": {
        color: theme.palette.error.main,
      },

      "& .fake-table": {
        "& div": {
          fontWeight: "bold",
          fontSize: "0.7rem",
          padding: theme.spacing(0.5),
          color: theme.customPalette.text.primary,
        },

        "& >*:nth-child(6n + 1),>*:nth-child(6n + 2),>*:nth-child(6n + 3)": {
          "backgroundColor": theme.customPaper.background.cardBackgroundLight,
          "& *": {
            backgroundColor: theme.customPaper.background.cardBackgroundLight,
          },
        },
      },
    },
  })
);

type CardAdminInstallationGroupProps = {
  title?: string;
  groupType: number;
  handleClick: (e: React.MouseEvent) => void;
  organization?: string;
  directorShip?: string;
  numberOfActiveUsers?: number;
  numberOfActiveInstallations?: number;
  inclusionDate?: Date;
  expirationDate?: Date;
  permissions?: string[];
};

function CardAdminInstallationGroup(props: CardAdminInstallationGroupProps) {
  const {
    title,
    groupType,
    handleClick,
    organization,
    directorShip,
    numberOfActiveUsers,
    numberOfActiveInstallations,
    inclusionDate,
    expirationDate,
    permissions,
  } = props;

  const classes = useStyles();

  return (
    <Card className={classes.root} elevation={5}>
      {/* Header */}
      <div
        className={
          groupType === ADMIN || groupType === INTERNAL || groupType === SUPER_ADMIN
            ? classes.greenHeader
            : classes.header
        }
      >
        <Typography component="h2">{title}</Typography>

        <Tooltip title="Editar configurações deste grupo de instalações">
          <IconButton onClick={handleClick}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      </div>
      <DottedLine />

      {/* Main Content */}

      <div className={classes.main}>
        <div className="grid">
          <div className={"flex-column"}>
            <Typography>Organização</Typography>
            <Typography className="large">{organization || EMPTY_STRING_LABEL}</Typography>
          </div>

          <div className={"flex-column"}>
            <Typography className="small">Diretoria</Typography>
            <Typography>{directorShip || EMPTY_STRING_LABEL}</Typography>
          </div>

          <div className={"flex"}>
            <div className={clsx("flex-column", "margin-right")}>
              <Typography className="small">Usuários ativos</Typography>
              <Typography>{(numberOfActiveUsers && padN(numberOfActiveUsers)) || 0}</Typography>
            </div>

            <div className={"flex-column"}>
              <Typography className="small">Instalações</Typography>
              <Typography>
                {(numberOfActiveInstallations && padN(numberOfActiveInstallations)) || 0}
              </Typography>
            </div>
          </div>

          <div className={"flex-column"}>
            <Typography className="small">Data de inclusão</Typography>
            <Typography>{formatDateTime(inclusionDate, "dd/MM/yyyy")}</Typography>
          </div>

          <div className={"flex-column"}></div>

          {expirationDate && (
            <div className={"flex-column"}>
              <Typography className="small expiration-color">Data de expiração</Typography>
              <Typography className="expiration-color">
                {formatDateTime(expirationDate, "dd/MM/yyyy")}
              </Typography>
            </div>
          )}
        </div>

        {/* Permissions group */}
        <div className="margin-top">
          <Typography component="h5">Permissões</Typography>
          <Grid container className="fake-table">
            {permissions?.map((permission, idx) => (
              <Grid item xs={4} key={`permission_${permission}_${idx}`}>
                <Typography component="div">{permission}</Typography>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
    </Card>
  );
}

export default CardAdminInstallationGroup;
